import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import './dashboard.css';
import TicketTableDashboard from './TicketTableDashboard';
import { useTranslation } from 'react-i18next';
import { 
    FaTicketAlt, 
    FaRobot, 
    FaBoxOpen, 
    FaShoppingCart, 
    FaChartLine,
    FaExternalLinkAlt,
    FaExclamationCircle,
    FaUsers,
    FaTrophy,
    FaSmile,
    FaMoneyBillWave,
    FaExclamationTriangle,
    FaBox,
    FaPlusCircle,
    FaShieldAlt,
    FaCheckCircle,
    FaCircle
} from 'react-icons/fa';
import { Pie } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    ArcElement,
    Tooltip,
    Legend
} from 'chart.js';

ChartJS.register(
    ArcElement,
    Tooltip,
    Legend
);

const Dashboard = () => {
    const [user, setUser] = useState(undefined);
    const [tickets, setTickets] = useState([]);
    const [assignedTickets, setAssignedTickets] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [assignedCurrentPage, setAssignedCurrentPage] = useState(1);
    const [paginationInfo, setPaginationInfo] = useState(null);
    const [assignedPaginationInfo, setAssignedPaginationInfo] = useState(null);
    const [allTickets, setAllTickets] = useState([]);
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    const [monthlyUsage, setMonthlyUsage] = useState({ tickets_used: 0, ticket_limit: 0, extra_tickets_purchased: 0, remaining_extra_tickets: 0 });
    
    // Add this line with your other state declarations
    const [isSearchActive, setIsSearchActive] = useState(false);
    
    const [ticketStats, setTicketStats] = useState({
        total_tickets: 0,
        ai_resolved_tickets: 0,
        assigned_tickets: 0,
        open_tickets: 0,
        pending_tickets: 0,
        closed_tickets: 0,
        resolved_tickets: 0,
        automation_ratio: 0,
        automated_tickets: 0,
        remaining_tickets: 0,
        waiting_carrier_response: 0,
        new_tickets: 0,
        affected_tickets: 0,
        waiting_client_response: 0,
        user_type: ''
    });
    const [orders, setOrders] = useState([]);
    const [returns, setReturns] = useState([]);
    const [attentionProducts, setAttentionProducts] = useState([]);
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [searchParams, setSearchParams] = useSearchParams();
    const wsRef = useRef(null);
    const reconnectTimeoutRef = useRef(null);
    const [expandedCards, setExpandedCards] = useState({});
    const [tagsStats, setTagsStats] = useState(null);
    const [clientStats, setClientStats] = useState({
        total_clients: 0,
        total_tickets: 0,
        most_active_client: null,
        clients: []
    });
    const [isStatsCardCollapsed, setIsStatsCardCollapsed] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [shopStats, setShopStats] = useState(null);
    const [isShopConfigured, setIsShopConfigured] = useState(true);

    // Brand-related state
    const [brands, setBrands] = useState([]);
    const [selectedBrand, setSelectedBrand] = useState(
        searchParams.get('selectedBrand') ? parseInt(searchParams.get('selectedBrand')) : null
    );

    // Add state for current filters - updated structure
    const [currentFilters, setCurrentFilters] = useState({
        topics: [], // Will be set based on user type
        separate_assigned: true // New parameter for backend
    });

    // Add state to track current view mode
    const [currentViewMode, setCurrentViewMode] = useState('unassigned'); // 'unassigned' | 'assigned'

    // State for the new ticket creation overlay
    const [isCreateTicketOverlayOpen, setIsCreateTicketOverlayOpen] = useState(false);
    const [newTicketData, setNewTicketData] = useState({ tracking: '', message: '', ticket_topic_id: '' });
    const [isSubmittingTicket, setIsSubmittingTicket] = useState(false);
    const [submitTicketError, setSubmitTicketError] = useState(null);
    const [submitTicketSuccess, setSubmitTicketSuccess] = useState(null);

    // State for OTP change overlay
    const [isOtpChangeOverlayOpen, setIsOtpChangeOverlayOpen] = useState(false);
    const [newOtpCode, setNewOtpCode] = useState('');
    const [isSubmittingOtp, setIsSubmittingOtp] = useState(false);
    const [submitOtpError, setSubmitOtpError] = useState(null);
    const [submitOtpSuccess, setSubmitOtpSuccess] = useState(null);

    const isSearchActiveRef = useRef(false);

    useEffect(() => {
        isSearchActiveRef.current = isSearchActive;
    }, [isSearchActive]);

    const connectWebSocket = () => {
        const token = localStorage.getItem('access_token');
        if (!token) return;

        // Check token expiration before connecting
        try {
            const tokenPayload = JSON.parse(atob(token.split('.')[1]));
            if (tokenPayload.exp && tokenPayload.exp * 1000 < Date.now()) {
                console.log('Token has expired, not connecting WebSocket');
                // Clear session and redirect to login if needed
                localStorage.removeItem('access_token');
                navigate('/');
                return;
            }
        } catch (error) {
            console.error('Error parsing token:', error);
            return;
        }

        wsRef.current = new WebSocket(`wss://${window.location.host}/api/ws?token=${token}`);
        
        wsRef.current.onopen = (event) => {
            console.log('WebSocket connection established:', event);
            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current);
                reconnectTimeoutRef.current = null;
            }
        };

        wsRef.current.onerror = (error) => {
            console.error('WebSocket error:', error);
        };

        wsRef.current.onclose = (event) => {
            console.log('WebSocket connection closed:', event);
            console.log('Close code:', event.code);
            console.log('Close reason:', event.reason);
            
            // Check if closure was due to authentication error (typical codes for auth issues)
            if (event.code === 1008 || event.code === 4000 || event.code === 4001 || 
                event.reason?.toLowerCase().includes('token') || 
                event.reason?.toLowerCase().includes('auth')) {
                console.log('WebSocket closed due to authentication issue, not reconnecting');
                // Optionally redirect to login
                localStorage.removeItem('access_token');
                navigate('/');
                return;
            }

            reconnectTimeoutRef.current = setTimeout(connectWebSocket, 5000);
        };

        wsRef.current.onmessage = (event) => {
            console.log('WebSocket message received:', event.data);
            if (event.data === 'ping') {
                wsRef.current.send('pong');
            } else {
                // Use ref instead of stale closure variable
                if (!isSearchActiveRef.current) {
                    const token = localStorage.getItem('access_token');
                    if (token) {
                        fetchTickets(token, currentPage, currentFilters);
                    }
                }
            }
        };
    };

    useEffect(() => {
        const token = localStorage.getItem('access_token');
        if (!token) {
            navigate('/');
            return;
        }

        const fetchAllData = async () => {
            try {
                setIsLoading(true);
                
                const userData = await fetchUserData(token);
                
                // Set initial filters based on user type
                const initialFilters = {
                    topics: userData?.fa_account ? ['Après vente'] : [], // Default to postsales for fa_account, General for others
                    separate_assigned: true // Now all users can use separated format
                };
                setCurrentFilters(initialFilters);
                
                const promises = [
                    fetchTickets(token, 1, initialFilters), // Use initial filters based on user type
                    fetchMonthlyUsage(token),
                    fetchTicketStats(token)
                ];
                
                // Fetch brands if user has_brand is true
                if (userData?.has_brand) {
                    console.log('🚀 User has brands, fetching brand list...');
                    promises.push(fetchBrands(token));
                } else {
                    console.log('🚫 User does not have brands (has_brand:', userData?.has_brand, ')');
                }
                
                if (!userData?.edn_api_account) {
                    promises.push(
                        fetchOrders(token),
                        fetchAttentionProducts(),
                        fetchReturns(token),
                        fetchShopStatistics(token)
                    );
                } else {
                    promises.push(fetchTagsStatistics(token));
                }
                
                await Promise.all(promises);
                await fetchClientStats(token);

            } catch (error) {
                console.error('Error loading dashboard data:', error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchAllData();
        connectWebSocket();

        const pingInterval = setInterval(() => {
            if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
                wsRef.current.send('ping');
            }
        }, 30000);

        return () => {
            clearInterval(pingInterval);
            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current);
            }
            if (wsRef.current) {
                wsRef.current.close();
            }
        };
    }, [navigate]);

    const fetchUserData = async (token) => {
        try {
            const response = await axios.get('/api/users/user', {
                headers: { Authorization: `Bearer ${token}` }
            });
            console.log('👤 User data received:', response.data);
            console.log('🏷️ User has_brand:', response.data?.has_brand);
            setUser(response.data);
            if (response.data?._id) {
                localStorage.setItem('user_id', response.data._id);
            }
            return response.data;
        } catch (error) {
            console.error('Failed to fetch user data:', error);
            setUser(null);
            return null;
        }
    };

    const fetchMonthlyUsage = async (token) => {
        try {
            const response = await axios.get('/api/users/monthly-usage', {
                headers: { Authorization: `Bearer ${token}` }
            });
            setMonthlyUsage(response.data || { tickets_used: 0, ticket_limit: 0, extra_tickets_purchased: 0, remaining_extra_tickets: 0 });
        } catch (error) {
            console.error('Failed to fetch monthly usage:', error);
        }
    };

    const fetchTickets = async (token, page = 1, filters = null, assignedPage = 1) => {
        try {
            const ticketsPerPage = 25;
            
            // Use provided filters or current filters
            const activeFilters = filters || currentFilters;
            
            // Build query parameters
            const queryParams = new URLSearchParams({
                page: page.toString(),
                limit: ticketsPerPage.toString(),
            });

            // Only add separated parameters for non-employee users
            if (activeFilters.separate_assigned) {
                queryParams.append('assigned_page', assignedPage.toString());
                queryParams.append('assigned_limit', ticketsPerPage.toString());
                queryParams.append('separate_assigned', 'true');
            }

            // Add topics to query params if they exist
            if (activeFilters.topics && activeFilters.topics.length > 0) {
                activeFilters.topics.forEach(topic => {
                    queryParams.append('topics', topic);
                });
            }

            const response = await axios.get(`/api/tickets/tickets?${queryParams}`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data) {
                if (activeFilters.separate_assigned) {
                    // Handle separated response for non-employee users
                    setTickets(response.data.unassigned_tickets || []);
                    setAssignedTickets(response.data.assigned_tickets || []);
                    setPaginationInfo(response.data.unassigned_pagination);
                    setAssignedPaginationInfo(response.data.assigned_pagination);
                } else {
                    // Handle simple response for employee users
                    setTickets(response.data.tickets || []);
                    setAssignedTickets([]);
                    setPaginationInfo(response.data.pagination);
                    setAssignedPaginationInfo(null);
                }
                
                setCurrentPage(page);
                setAssignedCurrentPage(assignedPage);
                
                // Reset allTickets since we're now using backend filtering
                setAllTickets([]);
                setIsInitialLoad(false);
            } else {
                setTickets([]);
                setAssignedTickets([]);
                setPaginationInfo(null);
                setAssignedPaginationInfo(null);
            }
        } catch (error) {
            console.error('Failed to fetch tickets:', error);
            setTickets([]);
            setAssignedTickets([]);
            setPaginationInfo(null);
            setAssignedPaginationInfo(null);
        }
    };

    const fetchTicketStats = async (token) => {
        try {
            const response = await axios.get('/api/tickets/stats', {
                headers: { Authorization: `Bearer ${token}` }
            });
            console.log('API response:', response.data);
            setTicketStats(response.data || {
                total_tickets: 0,
                ai_resolved_tickets: 0,
                assigned_tickets: 0,
                open_tickets: 0,
                pending_tickets: 0,
                closed_tickets: 0,
                resolved_tickets: 0,
                automation_ratio: 0,
                automated_tickets: 0,
                remaining_tickets: 0,
                waiting_carrier_response: 0,
                new_tickets: 0,
                affected_tickets: 0,
                waiting_client_response: 0,
                user_type: ''
            });
        } catch (error) {
            console.error('Failed to fetch ticket stats:', error);
        }
    };

    // Transform order data from new API format to match component expectations (similar to OrdersList.js)
    const transformOrderData = (apiOrder) => {
        // Log the raw API order data
        console.log('🔍 Raw API Order Data:', JSON.stringify(apiOrder, null, 2));
        console.log('🎯 Risk Score in API Order:', apiOrder.risk_prediction?.risk_score);
        
        const transformedOrder = {
            id: apiOrder._id,
            order_number: apiOrder.order_number,
            created_at: apiOrder.created_at,
            customer: {
                // Fix: Use billing address name instead of order name
                name: apiOrder.billing_address?.name || apiOrder.shipping_address?.name || null,
                email: apiOrder.customer_email || null
            },
            line_items: apiOrder.line_items || [],
            total_price: apiOrder.order_total?.toString() || '0.00',
            financial_status: apiOrder.financial_status,
            fulfillment_status: apiOrder.fulfillment_status,
            // Replace source_marketplace with shipping_country
            shipping_country: apiOrder.shipping_address?.country || apiOrder.billing_address?.country || null,
            tracking: apiOrder.tracking || [],
            risk_prediction_score: apiOrder.risk_prediction?.risk_score || null,
            // Include any other fields that might be needed
            ...apiOrder
        };
        
        // Log the transformed order data
        console.log('✅ Transformed Order Data:', JSON.stringify(transformedOrder, null, 2));
        console.log('🎯 Risk Score in Transformed Order:', transformedOrder.risk_prediction_score);
        
        return transformedOrder;
    };

    const fetchOrders = async (token) => {
        try {
            const response = await axios.get('/api/orders_management/orders', {
                headers: { Authorization: `Bearer ${token}` },
                params: { limit: 5 }
            });
            if (response.data.status === "unconfigured") {
                setOrders([]);
            } else {
                // Transform the orders to match component expectations
                const transformedOrders = (response.data.orders || []).map(transformOrderData);
                setOrders(transformedOrders);
            }
        } catch (error) {
            console.error('Failed to fetch orders:', error);
            setOrders([]);
        } 
    };

    const fetchReturns = async (token) => {
        try {
            const response = await axios.get('/api/webhook/returns', {
                headers: { Authorization: `Bearer ${token}` }
            });
            const returnsData = response.data.returns || [];
            setReturns(returnsData.slice(0, 5));
        } catch (error) {
            console.error('Failed to fetch returns:', error);
            setReturns([]);
        }
    };

    const fetchAttentionProducts = async () => {
        try {
            const token = localStorage.getItem('access_token');
            const response = await axios.get('/api/shopify/get_products_needing_attention', {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.data.status === "unconfigured" || !response.data.products) {
                setAttentionProducts([]);
            } else {
                setAttentionProducts(response.data.products.slice(0, 5)); // Only get top 5
            }
        } catch (error) {
            console.error('Failed to fetch attention products:', error);
            setAttentionProducts([]);
        }
    };

    const fetchTagsStatistics = async (token) => {
        try {
            const response = await axios.get('/api/tickets/tickets-tags-stats', {
                headers: { Authorization: `Bearer ${token}` }
            });
            setTagsStats(response.data);
        } catch (error) {
            console.error('Error fetching tags statistics:', error);
        }
    };

    const fetchClientStats = async (token) => {
        try {
            const response = await axios.get('/api/tickets/client-stats', {
                headers: { Authorization: `Bearer ${token}` }
            });
            setClientStats(response.data);
        } catch (error) {
            console.error('Failed to fetch client stats:', error);
        }
    };

    const fetchShopStatistics = async (token) => {
        try {
            const response = await axios.get('/api/shopify/get_shop_statistics', {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (response.data.status === "unconfigured") {
                setIsShopConfigured(false);
                setShopStats(null);
            } else {
                setIsShopConfigured(true);
                setShopStats(response.data);
            }
        } catch (error) {
            console.error('Failed to fetch shop statistics:', error);
            setShopStats(null);
            setIsShopConfigured(false);
        }
    };

    const fetchBrands = async (token) => {
        console.log('🔍 fetchBrands called');
        try {
            const response = await axios.get('/api/brands/list', {
                headers: { Authorization: `Bearer ${token}` }
            });
            console.log('✅ Brand API response:', response.data);
            setBrands(response.data || []);
            // Set the first brand as default selected if brands exist
            if (response.data && response.data.length > 0) {
                console.log('🎯 Setting selected brand to:', response.data[0].id);
                setSelectedBrand(response.data[0].id);
            } else {
                console.log('❌ No brands found in response');
            }
        } catch (error) {
            console.error('❌ Failed to fetch brands:', error);
            setBrands([]);
        }
    };

    const handleTicketClick = async (ticketId) => {
        try {
            const token = localStorage.getItem('access_token');
            await axios.put(`/api/tickets/${ticketId}/status`, null, {
                headers: { Authorization: `Bearer ${token}` }
            });
            navigate(`/tickets/${ticketId}/messages`);
        } catch (error) {
            console.error('Failed to update ticket status:', error);
        }
    };

    const toggleCard = (cardId) => {
        setExpandedCards(prev => ({
            ...prev,
            [cardId]: !prev[cardId]
        }));
    };

    const formatReason = (reason) => {
        const reasons = {
            out_of_stock: 'Out of Stock',
            low_stock: 'Low Stock',
            overstock: 'Overstock',
            low_sales_velocity: 'Low Sales',
            high_discount: 'High Discount',
            missing_handle: 'SEO Issue',
            thin_content: 'Thin Content',
            no_images: 'No Images',
            few_images: 'Few Images',
            no_tags: 'No Tags',
            single_variant: 'Single Variant'
        };
        return reasons[reason] || reason;
    };

    const tagsChartData = tagsStats?.tags_statistics ? {
        labels: tagsStats.tags_statistics.map(item => item._id),
        datasets: [{
            data: tagsStats.tags_statistics.map(item => item.percentage),
            backgroundColor: [
                '#00ff9d', '#00ffff', '#ff00ff', 
                '#8000ff', '#ffff00', '#ff0000', '#0099ff'
            ],
        }],
    } : null;

    const toggleStatsCard = () => {
        setIsStatsCardCollapsed(!isStatsCardCollapsed);
    };

    // Handlers for the create ticket overlay
    const handleOpenCreateTicketOverlay = () => {
        setIsCreateTicketOverlayOpen(true);
        setSubmitTicketError(null);
        setSubmitTicketSuccess(null);
        setNewTicketData({ tracking: '', message: '', ticket_topic_id: '' });
    };

    const handleCloseCreateTicketOverlay = () => {
        setIsCreateTicketOverlayOpen(false);
    };

    const handleNewTicketInputChange = (e) => {
        const { name, value } = e.target;
        setNewTicketData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmitNewTicket = async (e) => {
        e.preventDefault();
        setIsSubmittingTicket(true);
        setSubmitTicketError(null);
        setSubmitTicketSuccess(null);
        const token = localStorage.getItem('access_token');

        if (!newTicketData.ticket_topic_id || isNaN(parseInt(newTicketData.ticket_topic_id, 10))) {
            setSubmitTicketError(t('ticketTopicIdMustBeNumber', 'Ticket Topic ID must be a valid number.'));
            setIsSubmittingTicket(false);
            return;
        }

        const payload = {
            ...newTicketData,
            ticket_topic_id: parseInt(newTicketData.ticket_topic_id, 10)
        };

        try {
            const response = await axios.post('/api/tickets/forward_to_envoidunet_create', payload, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setSubmitTicketSuccess(t('ticketCreatedSuccessfully', 'Ticket created successfully!') + (response.data?.ticket_id ? ` ID: ${response.data.ticket_id}` : ''));
            setNewTicketData({ tracking: '', message: '', ticket_topic_id: '' });
            setTimeout(() => {
                handleCloseCreateTicketOverlay();
            }, 2500);
        } catch (error) {
            console.error('Failed to create EDN ticket:', error);
            setSubmitTicketError(error.response?.data?.detail || t('failedToCreateEdnTicket', 'Failed to create ticket. Please try again.'));
        } finally {
            setIsSubmittingTicket(false);
        }
    };

    // Handlers for OTP change overlay
    const handleOpenOtpChangeOverlay = () => {
        setIsOtpChangeOverlayOpen(true);
        setSubmitOtpError(null);
        setSubmitOtpSuccess(null);
        setNewOtpCode('');
    };

    const handleCloseOtpChangeOverlay = () => {
        setIsOtpChangeOverlayOpen(false);
        setNewOtpCode('');
        setSubmitOtpError(null);
        setSubmitOtpSuccess(null);
    };

    const handleSubmitOtpChange = async (e) => {
        e.preventDefault();
        setIsSubmittingOtp(true);
        setSubmitOtpError(null);
        setSubmitOtpSuccess(null);
        
        const token = localStorage.getItem('access_token');

        if (!newOtpCode.trim()) {
            setSubmitOtpError(t('otpCodeRequired', 'OTP code is required.'));
            setIsSubmittingOtp(false);
            return;
        }

        try {
            await axios.put('/api/users/user/otp', 
                { otp_code: newOtpCode },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            
            setSubmitOtpSuccess(t('otpUpdatedSuccessfully', 'OTP code updated successfully!'));
            setNewOtpCode('');
            setTimeout(() => {
                handleCloseOtpChangeOverlay();
            }, 2500);
        } catch (error) {
            console.error('Failed to update OTP code:', error);
            setSubmitOtpError(
                error.response?.data?.detail || 
                t('failedToUpdateOtp', 'Failed to update OTP code. Please try again.')
            );
        } finally {
            setIsSubmittingOtp(false);
        }
    };

    const handleBrandChange = (brandId) => {
        console.log('🔄 Brand changed to:', brandId);
        setSelectedBrand(brandId);
        
        // Update URL parameter for brand selection
        const newParams = new URLSearchParams(searchParams);
        if (brandId) {
            newParams.set('selectedBrand', brandId.toString());
        } else {
            newParams.delete('selectedBrand');
        }
        setSearchParams(newParams, { replace: true });
    };

    const handleFilterChange = (newFilters) => {
        const updatedFilters = { ...currentFilters, ...newFilters };
        setCurrentFilters(updatedFilters);
        
        // Update view mode based on filter change
        if (newFilters.viewMode !== undefined) {
            setCurrentViewMode(newFilters.viewMode);
        }
        
        const token = localStorage.getItem('access_token');
        if (token) {
            fetchTickets(token, 1, updatedFilters, 1); // Reset both pages when filtering
        }
    };

    const handlePageChange = (newPage) => {
        const token = localStorage.getItem('access_token');
        if (token && newPage > 0) {
            if (currentViewMode === 'assigned') {
                // Page change for assigned tickets
                if (newPage <= (assignedPaginationInfo?.total_pages || 1)) {
                    fetchTickets(token, currentPage, currentFilters, newPage);
                }
            } else {
                // Page change for unassigned tickets
                if (newPage <= (paginationInfo?.total_pages || 1)) {
                    fetchTickets(token, newPage, currentFilters, assignedCurrentPage);
                }
            }
        }
    };

    // Add risk score helper functions (similar to OrdersList.js)
    const getRiskScoreIcon = (score) => {
        console.log('🎯 getRiskScoreIcon called with:', { score, type: typeof score });
        
        if (score === null || score === undefined) return <FaCircle className="risk-icon risk-unknown" />;
        
        if (score >= 0.8) return <FaExclamationTriangle className="risk-icon risk-high" />;
        if (score >= 0.6) return <FaExclamationCircle className="risk-icon risk-medium" />;
        if (score >= 0.4) return <FaShieldAlt className="risk-icon risk-low" />;
        return <FaCheckCircle className="risk-icon risk-safe" />;
    };

    const getRiskScoreClass = (score) => {
        console.log('🎯 getRiskScoreClass called with:', { score, type: typeof score });
        
        if (score === null || score === undefined) return 'una-risk-unknown';
        
        if (score >= 0.8) return 'una-risk-high';
        if (score >= 0.6) return 'una-risk-medium';
        if (score >= 0.4) return 'una-risk-low';
        return 'una-risk-safe';
    };

    const formatRiskScore = (score) => {
        console.log('🎯 formatRiskScore called with:', { score, type: typeof score });
        
        if (score === null || score === undefined) return '-';
        return (score * 100).toFixed(0) + '%';
    };

    // Add function to get risk score text for badges
    const getRiskScoreText = (score) => {
        console.log('🎯 getRiskScoreText called with:', { score, type: typeof score });
        
        if (score === null || score === undefined) return '-';
        
        if (score >= 0.8) return 'High';
        if (score >= 0.6) return 'Medium';
        if (score >= 0.4) return 'Low';
        return 'Safe';
    };

    if (isLoading) {
        return (
            <div className="una-dashboard-loading-screen">
                <div className="una-loading-content">
                    <div className="una-loading-logo">
                        <img src="/bgless_logo.png" alt="Unanimity AI Logo" />
                    </div>
                    <h2>{t('loadingDashboard')}</h2>
                    <div className="una-loading-progress">
                        <div className="una-progress-bar">
                            <div className="una-progress-glow"></div>
                        </div>
                    </div>
                    <div className="una-loading-metrics">
                        <div className="una-metric-item">
                            <FaTicketAlt />
                            <span>{t('loadingTickets')}</span>
                        </div>
                        <div className="una-metric-item">
                            <FaShoppingCart />
                            <span>{t('loadingOrders')}</span>
                        </div>
                        <div className="una-metric-item">
                            <FaChartLine />
                            <span>{t('loadingStats')}</span>
                        </div>
                        <div className="una-metric-item">
                            <FaBoxOpen />
                            <span>{t('loadingReturns')}</span>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="una-dashboard-container">
            {/* OTP Change Button - Top Right Corner */}
            {user?.fa_account && (
                <div className="una-otp-change-button-container">
                    <button 
                        className="una-otp-change-button"
                        onClick={handleOpenOtpChangeOverlay}
                        title={t('changeOtpCode', 'Change OTP Code')}
                    >
                        <FaRobot />
                        {t('changeOtp', 'Change OTP')}
                    </button>
                </div>
            )}

            {/* Show stats header for edn_api_account users and admin employees */}
            {(user?.edn_api_account || (user?.role === 'employee' && user?.admin_role === 'admin')) && (
                <div className="una-dashboard-header">
                    <div className="una-dashboard-stats">
                        <div className="una-stats-card">
                            {!isStatsCardCollapsed && (
                                <div className="una-stats-card-content">
                                    <div className="una-stats-section">
                                        <div className="dashboard-stats-grid">
                                            <div className="una-metric-item primary">
                                                <div className="una-metric-icon primary">
                                                    <FaTicketAlt />
                                                </div>
                                                <div className="una-metric-details">
                                                    <span className="una-metric-value primary">{ticketStats.new_tickets}</span>
                                                    <span className="una-metric-label">{t('newTickets')}</span>
                                                </div>
                                            </div>
                                            
                                            <div className="una-metric-item secondary">
                                                <div className="una-metric-icon secondary">
                                                    <FaRobot />
                                                </div>
                                                <div className="una-metric-details">
                                                    <span className="una-metric-value secondary">{ticketStats.automated_tickets}</span>
                                                    <span className="una-metric-label">{t('automatedTickets')}</span>
                                                </div>
                                            </div>
                                            
                                            <div className="una-metric-item secondary">
                                                <div className="una-metric-icon secondary">
                                                    <FaTicketAlt />
                                                </div>
                                                <div className="una-metric-details">
                                                    <span className="una-metric-value secondary">{ticketStats.waiting_client_response}</span>
                                                    <span className="una-metric-label">{t('waitingClientResponse')}</span>
                                                </div>
                                            </div>
                                            
                                            <div className="una-metric-item tertiary">
                                                <div className="una-metric-icon tertiary">
                                                    <FaTicketAlt />
                                                </div>
                                                <div className="una-metric-details">
                                                    <span className="una-metric-value tertiary">{ticketStats.waiting_carrier_response}</span>
                                                    <span className="una-metric-label">{t('waitingCarrierResponse')}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            <div className="una-dashboard-main">
                {(user?.edn_api_account || user?.role === 'admin' || (user?.role === 'employee' && user?.admin_role === 'admin')) ? (
                    <TicketTableDashboard 
                        tickets={tickets} 
                        assignedTickets={assignedTickets}
                        user={user}
                        onTicketClick={handleTicketClick}
                        hasFaAccount={user?.fa_account}
                        onCreateTicket={handleOpenCreateTicketOverlay}
                        brands={brands}
                        selectedBrand={selectedBrand}
                        onBrandChange={handleBrandChange}
                        pagination={paginationInfo}
                        assignedPagination={assignedPaginationInfo}
                        onPageChange={handlePageChange}
                        onFilterChange={handleFilterChange}
                        currentViewMode={currentViewMode}
                        onSearchStateChange={setIsSearchActive}
                    />
                ) : !isShopConfigured ? (
                    <div className="una-unconfigured-shop">
                        <div className="una-welcome-message">
                            <FaShoppingCart className="una-welcome-icon" />
                            <h2>{t('welcomeToUnanimity')}</h2>
                            <p>{t('configureShopMessage')}</p>
                            <button 
                                className="una-primary-button"
                                onClick={() => navigate('/parameters')}
                            >
                                {t('configureShop')}
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="una-business-dashboard">
                        {/* Shop Statistics Overview */}
                        {shopStats && (
                            <div className="una-store-overview">
                                <div className="dashboard-stats-grid">
                                    <div className="una-stat-card revenue">
                                        <FaMoneyBillWave />
                                        <div className="una-stat-content">
                                            <h3>{t('shopStats.totalRevenue')}</h3>
                                            <p>${shopStats.orders?.total_revenue?.toFixed(2) || '0.00'}</p>
                                            <span>{t('shopStats.avgOrder')}: ${shopStats.orders?.average_order_value?.toFixed(2) || '0.00'}</span>
                                        </div>
                                    </div>

                                    <div className="una-stat-card orders">
                                        <FaShoppingCart />
                                        <div className="una-stat-content">
                                            <h3>{t('shopStats.totalOrders')}</h3>
                                            <p>{shopStats.orders?.total_orders || 0}</p>
                                            <span>{t('shopStats.avgItems')}: {shopStats.sales_analysis?.average_items_per_order?.toFixed(1) || '0.0'}</span>
                                        </div>
                                    </div>

                                    <div className="una-stat-card customers">
                                        <FaUsers />
                                        <div className="una-stat-content">
                                            <h3>{t('shopStats.customers')}</h3>
                                            <p>{shopStats.customers?.total_customers || 0}</p>
                                            <span>{t('shopStats.newCustomers')}: {shopStats.customers?.new_customers_30d || 0}</span>
                                        </div>
                                    </div>

                                    <div className="una-stat-card products">
                                        <FaBox />
                                        <div className="una-stat-content">
                                            <h3>{t('shopStats.products')}</h3>
                                            <p>{shopStats.products?.total_products || 0}</p>
                                            <span>{t('shopStats.variants')}: {shopStats.products?.total_variants || 0}</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Quick Alerts */}
                                <div className="una-alerts-section">
                                    {shopStats.products?.out_of_stock_count > 0 && (
                                        <div className="una-alert out-of-stock">
                                            <FaExclamationTriangle />
                                            <span>{shopStats.products.out_of_stock_count} {t('shopStats.outOfStock')}</span>
                                        </div>
                                    )}
                                    {shopStats.products?.low_stock_count > 0 && (
                                        <div className="una-alert low-stock">
                                            <FaExclamationTriangle />
                                            <span>{shopStats.products.low_stock_count} {t('shopStats.lowStock')}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                        
                        {/* Main dashboard grid with key information - now with 3 cards instead of 4 */}
                        <div className="una-dashboard-grid">
                            <div className="una-dashboard-card una-full-content">
                                <div className="una-card-header">
                                    <FaShoppingCart className="orders-icon" />
                                    <h3>{t('recentOrders')}</h3>
                                    <button onClick={() => navigate('/orders')} className="una-view-all">
                                        <FaExternalLinkAlt />
                                    </button>
                                </div>
                                <div className="una-card-content">
                                    {orders.slice(0, 5).map(order => (
                                        <div key={order.id} className="una-compact-item" onClick={() => navigate(`/order/${order.id}`)}>
                                            <div className="una-compact-info">
                                                <span className="una-primary-text">#{order.order_number}</span>
                                                <span className="una-secondary-text">{order.customer?.name}</span>
                                            </div>
                                            <div className="una-compact-details">
                                                {/* Add risk score display */}
                                                {(order.risk_prediction_score !== null && order.risk_prediction_score !== undefined) && (
                                                    <div className={`una-risk-score-compact ${getRiskScoreClass(order.risk_prediction_score)}`}>
                                                        {getRiskScoreIcon(order.risk_prediction_score)}
                                                        <span>{getRiskScoreText(order.risk_prediction_score)}</span>
                                                    </div>
                                                )}
                                                <span className="una-price-tag">${order.total_price}</span>
                                                <span className={`una-status-badge-dashboard ${order.financial_status?.toLowerCase()}`}>
                                                    {order.financial_status}
                                                </span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="una-dashboard-card una-full-content">
                                <div className="una-card-header">
                                    <FaBoxOpen className="returns-icon" />
                                    <h3>{t('recentReturns')}</h3>
                                    <button onClick={() => navigate('/returns')} className="una-view-all">
                                        <FaExternalLinkAlt />
                                    </button>
                                </div>
                                <div className="una-card-content">
                                    {returns.slice(0, 5).map(returnItem => (
                                        <div 
                                            key={returnItem._id} 
                                            className="una-compact-item"
                                            onClick={() => navigate(`/return/${returnItem._id}`)}
                                        >
                                            <div className="una-compact-info">
                                                <span className="una-primary-text">{returnItem.items?.[0]?.title || 'No title'}</span>
                                                <span className="una-secondary-text">#{returnItem.order_id}</span>
                                            </div>
                                            <div className="una-compact-details">
                                                <span className="una-price-tag">
                                                    €{(returnItem.items?.[0]?.price !== undefined ? 
                                                        Number(returnItem.items[0].price).toFixed(2) : 
                                                        '0.00')}
                                                </span>
                                                <span className={`una-status-badge-dashboard ${returnItem.status.replace('_', ' ').replace(' ', '-').toLowerCase()}`}>
                                                    {returnItem.status.replace('_', ' ')}
                                                </span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        
                            <div className="una-dashboard-card una-full-content">
                                <div className="una-card-header">
                                    <FaExclamationCircle className="attention-icon" />
                                    <h3>{t('productsNeedingAttention')}</h3>
                                    <button onClick={() => navigate('/shop-statistics')} className="una-view-all">
                                        <FaExternalLinkAlt />
                                    </button>
                                </div>
                                <div className="una-card-content">
                                    {attentionProducts.slice(0, 5).map(product => (
                                        <div 
                                            key={product.id} 
                                            className="una-compact-item"
                                            onClick={() => navigate(`/product/${product.id}`)}
                                        >
                                            <div className="una-compact-info">
                                                <span className="una-primary-text">{product.title}</span>
                                                <div className="una-compact-tags">
                                                    {product.health_status?.reasons?.slice(0, 2).map((reason, index) => (
                                                        <span key={index} className={`una-reason-tag ${reason}`}>
                                                            {formatReason(reason)}
                                                        </span>
                                                    ))}
                                                    {product.health_status?.reasons?.length > 2 && (
                                                        <span className="una-more-tag">+{product.health_status.reasons.length - 2}</span>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="una-compact-details">
                                                {product.health_status?.metrics?.sales_30_days !== undefined && (
                                                    <span className="una-metric-tag">
                                                        <FaShoppingCart />
                                                        {product.health_status.metrics.sales_30_days}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                        
                        {/* Ticket table shown after the main dashboard */}
                        <div className="una-tickets-section">
                            <div className="una-section-header">
                                <FaTicketAlt className="tickets-icon" />
                                <h2>{t('supportTickets')}</h2>
                            </div>
                            <TicketTableDashboard 
                                tickets={tickets} 
                                assignedTickets={assignedTickets}
                                user={user}
                                onTicketClick={handleTicketClick}
                                compact={true}
                                hasFaAccount={user?.fa_account}
                                onCreateTicket={handleOpenCreateTicketOverlay}
                                brands={brands}
                                selectedBrand={selectedBrand}
                                onBrandChange={handleBrandChange}
                                pagination={paginationInfo}
                                assignedPagination={assignedPaginationInfo}
                                onPageChange={handlePageChange}
                                onFilterChange={handleFilterChange}
                                currentViewMode={currentViewMode}
                                onSearchStateChange={setIsSearchActive}
                            />
                        </div>
                    </div>
                )}
            </div>

            {/* Overlay for creating EDN ticket */}
            {isCreateTicketOverlayOpen && (
                <div className="una-overlay" onClick={handleCloseCreateTicketOverlay}>
                    <div className="una-overlay-content" onClick={(e) => e.stopPropagation()}>
                        <h2>{t('createNewEdnTicketTitle', 'Create New EnvoiDuNet Ticket')}</h2>
                        <form onSubmit={handleSubmitNewTicket}>
                            <div className="una-form-group">
                                <label htmlFor="tracking">{t('trackingNumber', 'Tracking Number')}</label>
                                <input 
                                    type="text" 
                                    id="tracking" 
                                    name="tracking" 
                                    value={newTicketData.tracking}
                                    onChange={handleNewTicketInputChange}
                                    required 
                                />
                            </div>
                            <div className="una-form-group">
                                <label htmlFor="message">{t('message', 'Message')}</label>
                                <textarea 
                                    id="message" 
                                    name="message" 
                                    value={newTicketData.message}
                                    onChange={handleNewTicketInputChange}
                                    required 
                                    rows="4"
                                />
                            </div>
                            <div className="una-form-group">
                                <label htmlFor="ticket_topic_id">{t('ticketTopicId', 'Ticket Topic ID')}</label>
                                <input 
                                    type="number" 
                                    id="ticket_topic_id" 
                                    name="ticket_topic_id"
                                    value={newTicketData.ticket_topic_id}
                                    onChange={handleNewTicketInputChange}
                                    required 
                                />
                            </div>
                            {submitTicketError && <p className="una-error-message">{submitTicketError}</p>}
                            {submitTicketSuccess && <p className="una-success-message">{submitTicketSuccess}</p>}
                            <div className="una-form-actions">
                                <button type="button" onClick={handleCloseCreateTicketOverlay} className="una-button una-button-secondary">
                                    {t('cancel', 'Cancel')}
                                </button>
                                <button type="submit" className="una-button una-button-primary" disabled={isSubmittingTicket}>
                                    {isSubmittingTicket ? t('submitting', 'Submitting...') : t('createTicket', 'Create Ticket')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* OTP Change Overlay */}
            {isOtpChangeOverlayOpen && (
                <div className="una-overlay" onClick={handleCloseOtpChangeOverlay}>
                    <div className="una-overlay-content" onClick={(e) => e.stopPropagation()}>
                        <h2>{t('changeOtpCodeTitle', 'Change OTP Code')}</h2>
                        <form onSubmit={handleSubmitOtpChange}>
                            <div className="una-form-group">
                                <label htmlFor="newOtpCode">{t('newOtpCode', 'New OTP Code')}</label>
                                <input 
                                    type="text" 
                                    id="newOtpCode" 
                                    name="newOtpCode" 
                                    value={newOtpCode}
                                    onChange={(e) => setNewOtpCode(e.target.value)}
                                    placeholder={t('enterNewOtpCode', 'Enter your new OTP code')}
                                    required 
                                />
                                <small className="una-form-help">
                                    {t('otpCodeHelp', 'Enter the new OTP code from your authenticator app')}
                                </small>
                            </div>
                            {submitOtpError && <p className="una-error-message">{submitOtpError}</p>}
                            {submitOtpSuccess && <p className="una-success-message">{submitOtpSuccess}</p>}
                            <div className="una-form-actions">
                                <button 
                                    type="button" 
                                    onClick={handleCloseOtpChangeOverlay} 
                                    className="una-button una-button-secondary"
                                >
                                    {t('cancel', 'Cancel')}
                                </button>
                                <button 
                                    type="submit" 
                                    className="una-button una-button-primary" 
                                    disabled={isSubmittingOtp}
                                >
                                    {isSubmittingOtp ? 
                                        t('updating', 'Updating...') : 
                                        t('updateOtp', 'Update OTP')
                                    }
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Dashboard;